import { useEffect, useRef, useState } from "react";
import type { Mesh } from "@/lib/mesh3d";

/** Shaded, orbitable 3D preview (three.js, loaded on demand). Renders only when something changes, so it stays light on big meshes. */
export interface MeshSection { on: boolean; axis: 0 | 1 | 2; t: number } // t: 0..1 of the way along the axis the model is kept up to

export default function MeshView3D({ mesh, wireframe, autoRotate, resetSignal = 0, measure = false, onMeasure, section }: {
  mesh: Mesh; wireframe: boolean; autoRotate: boolean; resetSignal?: number;
  /** Click two points on the model: onMeasure gets the straight-line distance in model units, or null when the pick is cleared. */
  measure?: boolean; onMeasure?: (distance: number | null) => void;
  /** Cross-section: hides everything past the cut and shows the inside in orange. */
  section?: MeshSection;
}) {
  const host = useRef<HTMLDivElement>(null);
  const api = useRef<{ setWire: (b: boolean) => void; setSpin: (b: boolean) => void; reset: () => void; setMeasure: (b: boolean) => void; setSection: (s: MeshSection) => void } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const opts = useRef({ wireframe, autoRotate });
  opts.current = { wireframe, autoRotate };
  const live = useRef({ measure, section, onMeasure });
  live.current = { measure, section, onMeasure };

  useEffect(() => {
    let disposed = false;
    let cleanup = () => {};
    (async () => {
      try {
        const [THREE, { OrbitControls }] = await Promise.all([import("three"), import("three/examples/jsm/controls/OrbitControls.js")]);
        if (disposed || !host.current) return;
        const el = host.current;
        const w = el.clientWidth || 600, h = 420;
        const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
        renderer.localClippingEnabled = true;
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(w, h);
        el.appendChild(renderer.domElement);
        renderer.domElement.style.display = "block";
        renderer.domElement.style.borderRadius = "8px";
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x1f2430);
        const camera = new THREE.PerspectiveCamera(40, w / h, 0.01, 1e6);

        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(mesh.vertices), 3));
        geo.setIndex(new THREE.BufferAttribute(new Uint32Array(mesh.triangles.flat()), 1));
        geo.computeVertexNormals();
        geo.computeBoundingBox();
        const box = geo.boundingBox!;
        const centre = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const radius = Math.max(size.x, size.y, size.z, 1e-9);
        // One clipping plane drives the cross-section; far away (keeps everything) until the section is switched on.
        const plane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 1e9);
        const solid = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0x6aa5ff, metalness: 0.1, roughness: 0.55, side: THREE.DoubleSide, flatShading: true, clippingPlanes: [plane] }));
        // Inside faces show in orange where the model is cut open.
        const inside = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0xff9d3c, side: THREE.BackSide, clippingPlanes: [plane] }));
        inside.visible = false;
        const wire = new THREE.LineSegments(new THREE.WireframeGeometry(geo), new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.35, clippingPlanes: [plane] }));
        wire.visible = opts.current.wireframe;
        const group = new THREE.Group();
        group.add(solid, inside, wire);
        group.position.sub(centre);
        scene.add(group);
        const grid = new THREE.GridHelper(radius * 3, 20, 0x556, 0x334);
        grid.position.y = -size.y / 2;
        scene.add(grid);
        scene.add(new THREE.HemisphereLight(0xffffff, 0x223344, 1.1));
        const key = new THREE.DirectionalLight(0xffffff, 1.6); key.position.set(1, 2, 1.5); scene.add(key);
        const fill = new THREE.DirectionalLight(0xffffff, 0.5); fill.position.set(-1.5, -0.5, -1); scene.add(fill);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        const home = () => { camera.position.set(radius * 1.9, radius * 1.3, radius * 2.3); controls.target.set(0, 0, 0); camera.near = radius / 1000; camera.far = radius * 100; camera.updateProjectionMatrix(); controls.update(); };
        home();
        controls.autoRotate = opts.current.autoRotate; controls.autoRotateSpeed = 1.5;
        let raf = 0;
        // Keep the loop running only while the model is spinning or being moved.
        let idleFrames = 0;
        const frame = () => { controls.update(); renderer.render(scene, camera); };
        const tick = () => { frame(); if (controls.autoRotate || idleFrames-- > 0) raf = requestAnimationFrame(tick); else raf = 0; };
        const kick = () => { idleFrames = 60; if (!raf) raf = requestAnimationFrame(tick); };
        controls.addEventListener("change", kick);
        controls.addEventListener("start", kick);
        kick();
        const pointR = radius * 0.012;
        const marks = new THREE.Group();
        scene.add(marks);
        const picks: import("three").Vector3[] = [];
        const clearPicks = (redraw = true) => {
          picks.length = 0;
          marks.children.slice().forEach((c) => { marks.remove(c); (c as import("three").Mesh).geometry?.dispose(); ((c as import("three").Mesh).material as import("three").Material)?.dispose(); });
          if (redraw) kick();
        };
        const applySection = (sec: MeshSection) => {
          const on = sec.on && sec.t < 0.999;
          const ax = [new THREE.Vector3(-1, 0, 0), new THREE.Vector3(0, -1, 0), new THREE.Vector3(0, 0, -1)][sec.axis];
          const extent = [size.x, size.y, size.z][sec.axis];
          plane.normal.copy(ax);
          plane.constant = on ? -extent / 2 + sec.t * extent : 1e9; // keeps points whose coordinate on the axis is below the cut
          if (inside.visible !== on) {
            inside.visible = on;
            (solid.material as import("three").MeshStandardMaterial).side = on ? THREE.FrontSide : THREE.DoubleSide;
            (solid.material as import("three").Material).needsUpdate = true;
          }
          kick();
        };
        const dom = renderer.domElement;
        const ray = new THREE.Raycaster();
        const ndc = new THREE.Vector2();
        let down: { x: number; y: number } | null = null;
        const onDown = (e: PointerEvent) => { down = { x: e.clientX, y: e.clientY }; };
        const onUp = (e: PointerEvent) => {
          const d = down; down = null;
          if (!live.current.measure || !d || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 5) return; // a drag is orbiting, not a pick
          const r = dom.getBoundingClientRect();
          ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
          ray.setFromCamera(ndc, camera);
          group.updateMatrixWorld(true);
          const hit = ray.intersectObject(solid, false).find((h) => !inside.visible || plane.distanceToPoint(h.point) >= -1e-9); // cut-away parts can't be picked
          if (!hit) return;
          if (picks.length >= 2) clearPicks();
          picks.push(hit.point.clone());
          const dot = new THREE.Mesh(new THREE.SphereGeometry(pointR, 12, 8), new THREE.MeshBasicMaterial({ color: picks.length === 1 ? 0x2ecc71 : 0xff4d4d, depthTest: false }));
          dot.position.copy(hit.point); dot.renderOrder = 10; marks.add(dot);
          if (picks.length === 2) {
            const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(picks), new THREE.LineBasicMaterial({ color: 0xffe066, depthTest: false }));
            line.renderOrder = 10; marks.add(line);
            live.current.onMeasure?.(picks[0].distanceTo(picks[1]));
          } else live.current.onMeasure?.(null);
          kick();
        };
        dom.addEventListener("pointerdown", onDown);
        dom.addEventListener("pointerup", onUp);
        if (live.current.measure) dom.style.cursor = "crosshair";
        if (live.current.section) applySection(live.current.section);
        api.current = {
          setMeasure: (b) => { dom.style.cursor = b ? "crosshair" : ""; if (!b) { clearPicks(); live.current.onMeasure?.(null); } },
          setSection: applySection,
          setWire: (b) => { wire.visible = b; kick(); },
          setSpin: (b) => { controls.autoRotate = b; kick(); },
          reset: () => { home(); kick(); },
        };
        const onResize = () => { const nw = el.clientWidth || w; renderer.setSize(nw, h); camera.aspect = nw / h; camera.updateProjectionMatrix(); kick(); };
        window.addEventListener("resize", onResize);
        cleanup = () => {
          cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); controls.dispose();
          dom.removeEventListener("pointerdown", onDown); dom.removeEventListener("pointerup", onUp); clearPicks(false);
          (inside.material as import("three").Material).dispose();
          geo.dispose(); (solid.material as import("three").Material).dispose(); wire.geometry.dispose(); renderer.dispose();
          renderer.domElement.remove(); api.current = null;
        };
      } catch (e) { setErr(e instanceof Error ? e.message : "3D preview isn't available in this browser (WebGL is off)."); }
    })();
    return () => { disposed = true; cleanup(); };
  }, [mesh]);

  useEffect(() => { api.current?.setWire(wireframe); }, [wireframe]);
  useEffect(() => { api.current?.setSpin(autoRotate); }, [autoRotate]);
  useEffect(() => { if (resetSignal) api.current?.reset(); }, [resetSignal]);
  useEffect(() => { api.current?.setMeasure(measure); }, [measure]);
  useEffect(() => { if (section) api.current?.setSection(section); }, [section?.on, section?.axis, section?.t]); // eslint-disable-line react-hooks/exhaustive-deps

  return err ? <p className="text-sm text-red-600">{err}</p> : <div ref={host} data-testid="mesh-view" className="w-full" />;
}
